import { AuthenticationTriggerWrapper } from '@/components/buttons/AuthenticationTriggerWrapper';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { ReservoirAction } from '@/utils/reservoir';
import { ButtonProps } from '@chakra-ui/react';
import { AcceptBidModal, AcceptBidStep } from '@sphere/reservoir-kit-ui';
import useTranslation from 'next-translate/useTranslation';
import { ReactElement, ReactNode, useMemo, type FC } from 'react';
import { toast } from 'sonner';
import ActionDisabledTooltip from './ActionDisabledTooltip';

type Props = ButtonProps & {
  tokenId?: string;
  bidId?: string;
  collectionId?: string;
  openState?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  trigger: ReactElement<ReactNode>;
  mutate?(): void;
};

const AcceptBid: FC<Props> = ({ tokenId, bidId, collectionId, openState, trigger, mutate }) => {
  const { t } = useTranslation('common');

  const marketplaceChain = useMarketplaceChain();

  const WrappedTrigger = (
    <ActionDisabledTooltip action={ReservoirAction.AcceptBid}>{trigger}</ActionDisabledTooltip>
  );

  const tokens = useMemo(() => {
    return collectionId && tokenId
      ? [
          {
            collectionId: collectionId,
            tokenId: tokenId,
            bidIds: bidId ? [bidId] : undefined,
          },
        ]
      : [];
  }, [collectionId, tokenId, bidId]);

  return (
    <AuthenticationTriggerWrapper trigger={WrappedTrigger}>
      <AcceptBidModal
        trigger={WrappedTrigger}
        openState={openState}
        tokens={tokens}
        chainId={marketplaceChain.id}
        onClose={(data, stepData, currentStep) => {
          if (mutate && currentStep == AcceptBidStep.Complete) {
            mutate();
          }
        }}
        onBidAccepted={() => {
          toast.success(t('toast.accept-offer.success.title'), {
            description: t('toast.accept-offer.success.description'),
          });
        }}
        onBidAcceptError={(error: any) => {
          if (error?.type === 'price mismatch') {
            toast.error(t('toast.accept-offer.price-mismatch.title'), {
              description: t('toast.accept-offer.price-mismatch.description'),
            });
            return;
          }
          // Handle user rejection
          if (error?.code === 4001) {
            toast.error(t('toast.general.transaction-cancelled.title'), {
              description: t('toast.general.transaction-cancelled.description'),
            });
            return;
          }
          toast.error(t('toast.accept-offer.transaction-incomplete.title'));
        }}
      />
    </AuthenticationTriggerWrapper>
  );
};

export default AcceptBid;
